import { z } from 'zod';

export type FulfillmentProviderSelection = {
  id: string;
  provider?: string | null;
  service?: string | null;
  packageValue?: string | null;
  credentials_id?: string | null;
};

/**
 * What the fulfillment form asks for depends on the shipment: a package that
 * hides its size is not measured, and only a customs form needs a content type.
 */
export type ShippingFormContext = {
  dimensions: boolean;
  weight: boolean;
  isInternational: boolean;
};

/* The legacy panel's masked inputs hand back strings, the design-system fields
   numbers, and a reset field is null. All three mean the same measurement. */
const measurement = z
  .union([z.number(), z.string(), z.null()])
  .optional()
  .transform(value => {
    if (value === null || value === undefined || value === '') {
      return undefined;
    }
    const parsed = Number(value);
    return Number.isNaN(parsed) ? undefined : parsed;
  });

function requiredText(message: string) {
  return z
    .string({ required_error: message, invalid_type_error: message })
    .trim()
    .min(1, message);
}

/* Held as `''` until a warehouse is picked, so the object's own type error is
   what the field shows first: it has to read as the missing warehouse. */
const WAREHOUSE_REQUIRED = 'Fulfillment Provider is required';

const providerSelection = z.object(
  {
    id: requiredText(WAREHOUSE_REQUIRED),
    provider: z.string().nullish(),
    service: z.string().nullish(),
    packageValue: z.string().nullish(),
    credentials_id: z.string().nullish(),
  },
  {
    required_error: WAREHOUSE_REQUIRED,
    invalid_type_error: WAREHOUSE_REQUIRED,
  },
);

const baseShippingSchema = z.object({
  fulfillment_provider_id: providerSelection,
  pounds: measurement.pipe(z.number().min(0).optional()),
  ounces: measurement.pipe(z.number().min(0).max(15).optional()),
  /* A carrier measures in whole inches, and the legacy schema floored at 1. */
  length: measurement.pipe(z.number().min(1).optional()),
  width: measurement.pipe(z.number().min(1).optional()),
  height: measurement.pipe(z.number().min(1).optional()),
  provider: requiredText('Carrier is required'),
  service: requiredText('Service is required'),
  package: requiredText('Package is required'),
  origin_name: z.string().optional(),
  custom_origin: z.boolean().optional(),
  insurance: z.string().optional(),
  saveDefault: z.boolean().optional(),
  packing_list: z.boolean().optional(),
  shipping_speed: z.string().optional(),
  customsContentType: z.string().optional(),
  contentComments: z.string().optional(),
  signature: z.boolean().optional(),
  /* Defaulted so that the submit path always has an object to strip from. */
  options: z
    .object({
      insurance: z.string().optional(),
      hazmat: z.boolean().optional(),
      tax_code: z.string().optional(),
    })
    .default({}),
});

const DIMENSIONS = ['length', 'width', 'height'] as const;

/**
 * The fulfillment form on the details page. A factory rather than a constant:
 * which fields are required depends on the shipment, and the rules read better
 * as conditions than as a schema that has to ask its context at every field.
 */
export function createShippingSchema({
  dimensions,
  weight,
  isInternational,
}: ShippingFormContext) {
  return baseShippingSchema.superRefine((values, ctx) => {
    if (weight && !(values.pounds || values.ounces)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Weight should have value',
        path: ['pounds'],
      });
    }

    if (dimensions) {
      DIMENSIONS.forEach(side => {
        if (!values[side]) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `${side[0].toUpperCase()}${side.slice(1)} is required`,
            path: [side],
          });
        }
      });
    }

    /* Customs needs it whatever the carrier: required for every international
       shipment since `cf36784d1`, which narrowing to FedEx would undo. FedEx
       takes a free-text comment in its place, which predates that rule. */
    if (isInternational && !values.customsContentType?.trim()) {
      const fedexComment =
        values.provider.toLowerCase() === 'fedex' &&
        !!values.contentComments?.trim();

      if (!fedexComment) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Content Type is Required',
          path: ['customsContentType'],
        });
      }
    }
  });
}

/**
 * The form as it is held while being filled in: a measurement is whatever the
 * field that drew it stores. The schema turns those into numbers on submit.
 */
export type ShippingFormValues = z.input<typeof baseShippingSchema>;

/** A line the selected provider can ship, with the quantity still outstanding. */
export type FulfillmentItem = {
  _id: string;
  /** A V2 line's catalog item (null while unmapped); see catalogItemId. */
  catalog_item_id?: string | null;
  /** The order line's own title, which the marketplace sent. */
  title?: string;
  p_title?: string;
  item_name?: string;
  p_sku?: string;
  upc?: string;
  p_images?: { display_image?: string };
  g_attributes?: { short_title?: string };
  qty?: number;
  fulfilledQty?: number;
  newQty?: number;
  available?: boolean;
  /** Why the line is unavailable, when absence alone would read wrong. */
  unavailableReason?: string;
  checked?: boolean;
  alias?: string;
  loading?: boolean;
  hts_code?: string;
};

export function getItemTitle(item: FulfillmentItem): string {
  return (
    item.g_attributes?.short_title ||
    item.title ||
    item.p_title ||
    item.item_name ||
    item.p_sku ||
    'Item'
  );
}

/** What is left to ship on a line, which is also the most one may pick. */
export function getRemainingQty(item: FulfillmentItem): number {
  return Math.max((item.qty || 0) - (item.fulfilledQty || 0), 0);
}
